import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  QButton,
  QCloseButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelectItem,
  QStack,
  QText,
  useToastProvider,
} from '@qualio/ui-components';

import { QualityEvent } from '../../types';
import { EventTag } from '../../types/eventTag';
import { QualityEventCreate } from '../../types/qualityEvent';
import { Relation } from '../../types/relation';
import { User } from '../../types/user';
import EventForm from './components/EventForm';
import { DefaultErrorMessage, OpenEvent, EventOpened, EventNotOpened } from '../../displayStrings';
import { useEventMetadata } from '../../hooks/useEventMetadata';
import { useProducts } from '../../hooks/useProducts';
import { useRootCauses } from '../../hooks/useRootCauses';
import { linkedRegistryItemIds } from '../../utils/eventUtils';

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  event: QualityEvent;
  related: Relation[];
  suppliers?: QSelectItem[];
  selectsData: {
    eventTags: EventTag[];
    users: User[];
  };
  onSave: (payload: QualityEventCreate) => Promise<void>;
};

const EventOpenModal: React.FC<Props> = ({ isOpen, setIsOpen, selectsData, onSave, event, related, suppliers }) => {
  const { showToast } = useToastProvider();
  const { data: eventsMetadata } = useEventMetadata();
  const { data: productsList } = useProducts(linkedRegistryItemIds(event.products));
  const { data: rootCausesList } = useRootCauses(linkedRegistryItemIds(event.rootcauses));

  const formMethods = useForm<QualityEventCreate>({
    mode: 'onSubmit',
    resolver: zodResolver(QualityEventCreate),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = formMethods;

  const formHasErrors = !!Object.keys(errors).length;

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const onSubmit = useCallback(
    async (data: QualityEventCreate) => {
      try {
        await onSave(data);
        setIsOpen(false);
        showToast({
          title: EventOpened,
          description: `"${data.title}" was opened`,
          status: 'success',
        });
        reset();
      } catch {
        showToast({
          title: EventNotOpened,
          description: DefaultErrorMessage,
          status: 'error',
        });
      }
    },
    [onSave, showToast, setIsOpen, reset],
  );

  return (
    <QModal isOpen={isOpen} onClose={handleClose} size="xl">
      <QModalHeader>
        <QText>{OpenEvent}</QText>
        <QCloseButton onClick={handleClose} data-metrics="open-event-close-modal-button"></QCloseButton>
      </QModalHeader>
      <QModalBody>
        <QStack direction="column" color="gray.700" spacing={4}>
          <EventForm
            formMethods={formMethods}
            onSubmit={onSubmit}
            data={{
              ...selectsData,
              probabilities: eventsMetadata?.probabilities ?? [],
              severities: eventsMetadata?.severities ?? [],
              risks: eventsMetadata?.risks ?? [],
              products: productsList ?? [],
              rootCauses: rootCausesList ?? [],
              eventTypes: [],
            }}
            event={event}
            related={related}
            suppliers={suppliers}
            optionalFields={event.template_fields}
          />
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton onClick={handleClose} variant="outline" data-metrics="open-event-cancel-button">
          Cancel
        </QButton>
        <QButton
          onClick={() => handleSubmit(onSubmit)()}
          isDisabled={formHasErrors || isSubmitting}
          data-metrics="open-event-button"
        >
          Open event
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default EventOpenModal;
